import { queryOptions } from '@tanstack/react-query';

import { fetchBackend } from '../../apiClient';

// Re-export from modular structure
export * from './gitlab/queries';
export * from './gitlab/repositories';

export type GitlabConfig = {
  id: string;
  projectId: string;
  gitlabInstanceUrl: string;
  userId: number;
  username: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
};

export type GitlabConfigForList = {
  id: string;
  projectId: string;
  createdAt: string;
  updatedAt: string;
};

export const gitlabConfigQueries = {
  all: () => ['gitlab-configs'],
  byProject: (projectId: string) => [...gitlabConfigQueries.all(), projectId],
  list: (projectId: string) =>
    queryOptions({
      queryKey: ['gitlab-configs', projectId],
      queryFn: async (): Promise<GitlabConfig[]> => {
        const gitlabConfigs = await listGitlabConfigs(projectId);

        return gitlabConfigs;
      },
    }),
} as const;

export const listGitlabConfigs = async (
  projectId: string,
): Promise<GitlabConfig[]> => {
  const getResponse = await fetchBackend(`/gitlab-config/${projectId}`, {
    method: 'GET',
  });

  if (!getResponse.ok) {
    throw new Error(`Error: ${getResponse.statusText}`);
  }

  const response = await getResponse.json();

  // Backend returns {configurations: [...]} but we need the array directly
  return response.configurations ?? [];
};

export type GitlabOAuthResponse = {
  configurationId: string;
  username: string;
  expiresAt?: string;
};

export const exchangeCodeForToken = async (
  projectId: string,
  code: string,
): Promise<GitlabOAuthResponse> => {
  const redirectUri = `${window.location.origin}/gitlab-callback/authenticated`;

  const postResponse = await fetchBackend(
    `/gitlab-config/${projectId}/exchange-token`,
    {
      method: 'POST',
      body: JSON.stringify({ code, redirectUri }),
    },
  );

  if (!postResponse.ok) {
    throw new Error(`Error: ${postResponse.statusText}`);
  }

  return (await postResponse.json()) as GitlabOAuthResponse;
};

export const createGitlabConfiguration = async (
  projectId: string,
  gitlabToken: string,
): Promise<void> => {
  const postResponse = await fetchBackend(`/gitlab-config/${projectId}`, {
    method: 'POST',
    body: JSON.stringify({
      gitlabToken,
    }),
  });

  if (!postResponse.ok) {
    throw new Error(`Error: ${postResponse.statusText}`);
  }
};
